import { APITypes } from "@cur8/api-client";
import { fromAPI } from "@cur8/rich-entity";
import { useInfiniteQuery } from "@tanstack/react-query";
import { BookingResource } from "lib/api/booking-resource";
import { APIClient } from "lib/api/client";
import { useAPIClient } from "render/context/APIContext";
import { useSession } from "render/context/MSALContext";

type Params = Partial<
  Omit<
    APITypes.BookingListByResourceUriParams,
    "patientId | resourceURI | pageSize"
  >
>;

const queryKey = (patientId: string) => ["patient-slots", patientId];
const queryFn = async (
  apiClient: APIClient,
  patientId: string,
  options: Params
) => {
  const req = apiClient.booking.querySlots({
    patientId,
    resourceURI: BookingResource.B2CClinic.toString(),
    pageSize: 20,
    order: APITypes.SortOrder.Desc,
    ...options,
  });

  const response = await req.result;

  return {
    nextPage: response.nextPage,
    items: response.items.map(fromAPI.toBookingSlot),
  };
};

export function usePatientBookingsQuery(
  { continuationToken, ...restParams }: Params = {},
  options: Partial<{ onError: (err: unknown) => void; enabled: boolean }> = {}
) {
  const { patientId } = useSession();
  const apiClient = useAPIClient();

  const query = useInfiniteQuery(
    queryKey(patientId),
    ({ pageParam = continuationToken }) =>
      queryFn(apiClient, patientId, {
        ...restParams,
        continuationToken: pageParam,
      }),
    {
      enabled: options.enabled,
      getNextPageParam: ({ nextPage }) => nextPage,
      onError: options.onError,
    }
  );

  return query;
}
